// import { MOCK_USER_PERMISSIONS, USER_CARD_DISCREPANCIES } from '../user.mock';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, take } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { PaginatedResult } from 'src/app/app.model';

import type { Permission } from '@bciti/types';
import { ReceiptResponse, ReceiptItemSubscriptionResponse, ReceiptItemParkingResponse, ReceiptItemPrepaidParkingResponse, ItemSubscriptionPayload, PaymentPayload } from '@bciti/types';
import { State } from 'src/app/store';
import { Store } from '@ngrx/store';
import { organisationTagSelector } from 'src/app/global/store/global.selectors';
import { authOrgSelector } from 'src/app/auth/store/auth.selectors';
import { TranslateService } from '@ngx-translate/core';
import { Link } from 'src/app/global/global.model';
import { IPermissionScope } from 'src/app/auth/auth.model';
import { profileIdSelector, userRecordSelector } from '../store/user.selectors';
import { environment } from '../../../environments/environment';
import { UserService } from './user.abstract.service';
import {
    AdminRegisterPayload,
    CreateUserRecordPayload,
    FamilyLink,
    PatchApproveRefuseFamilyLink,
    PendingRequestCounts,
    Profile,
    ProfileResponse,
    UpdateUserRecordPayload,
    UserCardDiscrepanciesResponse,
    CardVerificationResponse,
    UserRecordResponse,
    UserResponse,
    GetUserActiveSubscriptionsResponse,
    LinkCardPayload,
    PostNewFamilyLink,
    RenewCardPayload,
    SubscribeCardPayload,
    UserCards,
    UserWallet,
    UserSearchQuery,
    PostUserFamilyLinkPayload,
    GetFamilyLinksItem,
    PostFamilyLinkPayload,
    UpdateFamilyLinkPayload,
    GetUserSubscriptionsResponse,
    BcitiPaginatedResult,
    UserRequest,
    GetUserRequests,
    Vehicule,
    UserActivityResponse,
    CardSearchResponse,
} from '../user.model';

@Injectable({
    providedIn: 'root',
})
export class UserServiceImpl implements UserService {
    private baseUrl: string = environment.API_GATEWAY_URL;

    private authServiceUrl: string = environment.AUTH_SERVICE_URL;

    public env = environment;

    private apiUrl: string;

    private permitUrl: string;

    private _user: string | undefined;

    public _organisation: string | undefined;

    public requestOptions;

    constructor(
        private http: HttpClient,
        private store: Store<State>,
        private translateService: TranslateService,
    ) {
        const myOptions = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8;',
            Authorization: 'Bearer 7acf7484-ad59-4cae-95c0-8de57c7948bd',
        });
        this.requestOptions = { headers: myOptions };

        this.apiUrl = this.env.API_PERMIT_URL;
        this.permitUrl = this.env.PERMIT_URL;
    }

    private getResolvedOrgTag$(): Observable<string> {
        return combineLatest([this.store.select(organisationTagSelector), this.store.select(authOrgSelector)]).pipe(
            map(([globalTag, authTag]) => globalTag || authTag),
            filter((tag): tag is string => !!tag),
            take(1),
        );
    }

    public set user(value: string | undefined) {
        this._user = value;
    }

    public set organisation(value: string | undefined) {
        this._organisation = value;
    }

    public getProfile(): Observable<UserRecordResponse> {
        return this.http.get(`${this.baseUrl}/profile`) as Observable<UserRecordResponse>;
    }

    public getUser(userid: string): Observable<UserRecordResponse> {
        return this.http.get(`${this.baseUrl}/profile/admin/${userid}`) as Observable<UserRecordResponse>;
    }

    public quickSearch(query: string, page = 1, pageSize = 10, filterBy?: string): Observable<PaginatedResult<UserResponse>> {
        return this.getResolvedOrgTag$().pipe(
            mergeMap(
                (orgKey) =>
                    this.http.get(`${this.baseUrl}/profile/fast-search?q=${query}&limit=${pageSize}&page=${page}&filterBy=${filterBy}`, {
                        headers: { 'x-organisation': orgKey },
                    }) as Observable<PaginatedResult<UserResponse>>,
            ),
            catchError((e) => {
                console.error(e);
                return of(undefined);
            }),
        );
    }

    public advancedSearch(queryObject: object, page = 1, pageSize = 10): Observable<PaginatedResult<UserResponse>> {
        const queryString = Object.entries(queryObject)
            .filter(([_, value]) => value !== '' && value !== null && value !== undefined)
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join('&');

        return this.getResolvedOrgTag$().pipe(
            mergeMap(
                (orgKey) =>
                    this.http.get(`${this.baseUrl}/profile/advanced-search?${queryString}&limit=${pageSize}&page=${page}`, {
                        headers: { 'x-organisation': orgKey },
                    }) as Observable<PaginatedResult<UserResponse>>,
            ),
            catchError((e) => {
                console.error(e);
                return of(undefined);
            }),
        );
    }

    public createUserRecord(payload: CreateUserRecordPayload): Observable<{ userId: string }> {
        return this.http.post<{ userId: string }>(`${this.authServiceUrl}/auth/admin/register`, {
            email: payload.email,
            firstName: payload.firstName,
            lastName: payload.lastName,
            orgId: payload.orgId,
            clientId: payload.clientId,
        });
    }

    public adminRegisterUser(payload: AdminRegisterPayload): Observable<{ userId: string }> {
        return this.http.post<{ userId: string }>(`${this.authServiceUrl}/auth/admin/register`, payload);
    }

    public updateUserRecord(payload: UpdateUserRecordPayload): Observable<UserRecordResponse> {
        return this.store.select(userRecordSelector).pipe(
            filter((userRecord) => !!userRecord?.id),
            take(1),
            mergeMap((userRecord) => this.http.patch(`${this.baseUrl}/profile/admin/${userRecord.id}`, payload) as Observable<UserRecordResponse>),
        );
    }

    public updateUserRecordById(id: string, payload: UpdateUserRecordPayload): Observable<UserRecordResponse> {
        return this.http.patch(`${this.baseUrl}/profile/admin/${id}`, payload) as Observable<UserRecordResponse>;
    }

    public uploadProfilePicture(file: File): Observable<string> {
        const formData = new FormData();
        formData.append('file', file);
        return this.store.select(userRecordSelector).pipe(
            take(1),
            mergeMap((userRecord) => this.http.post(`${this.baseUrl}/profile/admin/${userRecord.id}/picture`, formData) as Observable<{ url: string }>),
            map((o) => o.url),
        );
    }

    public uploadProfilePictureById(id: string, file: File): Observable<string> {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post(`${this.baseUrl}/profile/admin/${id}/picture`, formData).pipe(map((o: { url: string }) => o.url));
    }

    public adminManageRole(roleId: string, userId: string): Observable<Profile> {
        return this.http.patch(`${this.baseUrl}/access/user/role/${userId}/${roleId}`, {}).pipe(map((response: Profile) => response));
    }

    public removeUserRole(roleId: string, userId: string): Observable<any> {
        return this.getResolvedOrgTag$().pipe(
            mergeMap((orgKey) => this.http.delete(`${this.baseUrl}/access/user/role/${userId}/${roleId}`, { headers: { 'x-organisation': orgKey } }).pipe(map((res) => res as any))),
        );
    }

    public deleteUser(id: string): Observable<boolean> {
        return this.getResolvedOrgTag$().pipe(mergeMap((orgKey) => this.http.patch(`${this.baseUrl}/profile/${id}/organisation/remove/${orgKey}`, {}).pipe(map(() => true))));
    }

    public sendForgotPasswordEmail(email: string): Observable<boolean> {
        return this.http
            .post(`${this.authServiceUrl}/auth/forgot-password`, {
                email,
                orgId: environment.OIDC_ORG_ID,
            })
            .pipe(map(() => true));
    }

    public getProfileById(userId: string): Observable<ProfileResponse> {
        return this.http.get(`${this.baseUrl}/profile/admin/${userId}`) as Observable<ProfileResponse>;
    }

    public sendConfirmAccountEmail(email: string): Observable<boolean> {
        return this.http
            .post(`${this.authServiceUrl}/auth/resend-verify-email`, {
                email,
                orgId: environment.OIDC_ORG_ID,
            })
            .pipe(map(() => true));
    }

    public approveUserCard(userId: string, cardId: string, subType: string, selectedExternalId?: string, selectedCardNumber?: string): Observable<boolean> {
        const body: any = { subType };
        if (selectedExternalId) {
            body.selectedExternalId = selectedExternalId;
        }
        if (selectedCardNumber) {
            body.selectedCardNumber = selectedCardNumber;
        }
        return this.http.patch(`${this.baseUrl}/cards/approve/${cardId}/user/${userId}`, body).pipe(map((response) => response as boolean));
    }

    public approveNotConnectedUserCard(userId: string, cardId: string, subType: string, expDate: string, cardNumber: string): Observable<boolean> {
        return this.http.patch(`${this.baseUrl}/cards/approve/${cardId}/user/${userId}`, { subType, expDate, cardNumber }).pipe(map((response) => response as boolean));
    }

    public approveNotConnectedLinkUserCard(userId: string, cardId: string, subType: string, expDate: string): Observable<boolean> {
        return this.http.patch(`${this.baseUrl}/cards/approve/${cardId}/user/${userId}`, { subType, expDate }).pipe(map((response) => response as boolean));
    }

    public denyUserCard(userId: string, cardId: string, reason: string): Observable<boolean> {
        return this.http.patch(`${this.baseUrl}/cards/deny/${cardId}/user/${userId}`, { reason }).pipe(map((response) => response as boolean));
    }

    public deleteUserCard(userId: string, cardId: string): Observable<boolean> {
        return this.http.delete(`${this.baseUrl}/cards/${cardId}/user/${userId}`).pipe(map((response) => response as boolean));
    }

    public approveUserCardRenewal(userId: string, cardId: string, subType: string): Observable<boolean> {
        return this.http.patch(`${this.baseUrl}/cards/admin/renew/approve/${cardId}/user/${userId}`, { subType }).pipe(map((response) => response as boolean));
    }

    public approveNotConnectedUserCardRenewal(userId: string, cardId: string, subType: string, expDate: string, cardNumber: string): Observable<boolean> {
        return this.http.patch(`${this.baseUrl}/cards/admin/renew/approve/${cardId}/user/${userId}`, { subType, expDate, cardNumber }).pipe(map((response) => response as boolean));
    }

    public denyUserCardRenewal(userId: string, cardId: string, reason: string): Observable<boolean> {
        return this.http.patch(`${this.baseUrl}/cards/admin/renew/refuse/${cardId}/user/${userId}`, { reason }).pipe(map((response) => response as boolean));
    }

    public patchApproveRefuseFamilyLink(body: PatchApproveRefuseFamilyLink): Observable<boolean> {
        const headers = new HttpHeaders().set('x-application', 'admin');

        return this.http.patch(`${this.baseUrl}/profile/family/admin/confirm`, body, { headers }).pipe(map((response) => response as boolean));
    }

    public getUserFamily(userId: string): Observable<FamilyLink[]> {
        return this.http.get(`${this.baseUrl}/profile/family/admin/${userId}`).pipe(map((response) => response as FamilyLink[]));
    }

    public cardAddressExists(cardId: string): Observable<Record<string, any>> {
        return this.http.get(`${this.baseUrl}/cards/${cardId}/residency`).pipe(map((response) => response as Record<string, any>));
    }

    public getUserPermissions(): Observable<Permission[]> {
        return this.store.select(authOrgSelector).pipe(
            filter((orgKey) => !!orgKey),
            take(1),
            mergeMap((orgKey) => this.http.get(`${this.baseUrl}/access/permission`, { headers: { 'x-organisation': orgKey } }).pipe(map((response) => response as Permission[]))),
        );
    }

    public cardDiscrepancies(cardId: string, userId: string): Observable<UserCardDiscrepanciesResponse> {
        return this.http.get(`${this.baseUrl}/cards/${cardId}/user/${userId}/discrepancies`).pipe(map((response) => response as UserCardDiscrepanciesResponse));
    }

    public cardVerification(cardId: string, userId: string): Observable<CardVerificationResponse> {
        return this.http.get(`${this.baseUrl}/cards/${cardId}/user/${userId}/verification`).pipe(map((response) => response as CardVerificationResponse));
    }

    public updateProfile(payload: Partial<UpdateUserRecordPayload>): Observable<ProfileResponse> {
        return this.store.select(profileIdSelector).pipe(
            filter((id) => !!id),
            take(1),
            mergeMap((id) => this.http.patch(`${this.baseUrl}/profile/${id}`, payload) as Observable<ProfileResponse>),
        );
    }

    public getWallet(userId: string): Observable<UserWallet> {
        return this.http.get(`${this.baseUrl}/cards/${userId}`).pipe(map((response: any) => response as UserWallet));
    }

    public getFamilyMemberWallet(userId: string) {
        return this.http.get(`${this.baseUrl}/cards/family/${userId}`).pipe(map((r) => r as UserWallet));
    }

    public getFamilyMemberSubscriptions(userId: string) {
        return this.http.get(`${this.baseUrl}/profile/family/${userId}/subscriptions`).pipe(map((r) => r as GetUserActiveSubscriptionsResponse[]));
    }

    public familyPatchRenewalCard(payload: RenewCardPayload, userId: string): Observable<UserCards> {
        return this.http.patch(`${this.baseUrl}/cards/family/renew/${payload._id}/user/${userId}`, payload).pipe(map((response) => response as UserCards));
    }

    public postSubscribeCard(payload: SubscribeCardPayload, userId: string) {
        return this.http.post(`${this.baseUrl}/cards/admin/${userId}`, payload).pipe(map((response) => response as UserCards));
    }

    public patchRenewalCard(payload: RenewCardPayload) {
        return this.http.patch(`${this.baseUrl}/cards/admin/renew/${payload._id}/user/${payload.userId}`, payload).pipe(map((response) => response as UserCards));
    }

    public postLinkCard(payload: LinkCardPayload) {
        return this.http.post(`${this.baseUrl}/cards/link`, payload).pipe(map((response) => response));
    }

    public postLinkCardAdmin(payload: LinkCardPayload, userId: string) {
        return this.http.post(`${this.baseUrl}/cards/admin/link/${userId}`, payload).pipe(map((response) => response));
    }

    public getUserActiveSubscriptions(userId: string) {
        return this.http.get(`${this.baseUrl}/profile/admin/user/${userId}/subscriptions`).pipe(map((r) => r as GetUserActiveSubscriptionsResponse[]));
    }

    public getSystExtLink(connectorType: string) {
        return this.http.get(`${this.baseUrl}/profile/connection/${connectorType}`).pipe(map((r) => r as string));
    }

    public patchUpdateCard(payload: any, cardId: string) {
        return this.http.patch(`${this.baseUrl}/cards/${cardId}`, payload).pipe(map((r) => r));
    }

    public getOrganisationFeatures(tag: string) {
        return this.http
            .get(`${this.baseUrl}/configurations/features`, {
                headers: new HttpHeaders({
                    'x-organisation': tag,
                }),
            })
            .pipe(map((r) => r));
    }

    public familyPostSubscribeCard(payload: SubscribeCardPayload, id: string): Observable<UserCards> {
        return this.http.post(`${this.baseUrl}/cards/family/${id}`, payload).pipe(map((r) => r as UserCards));
    }

    public familyPostLinkCard(payload: LinkCardPayload, id: string): Observable<any> {
        return this.http.post(`${this.baseUrl}/cards/family/link/${id}`, payload).pipe(map((r) => r));
    }

    public getAllCoreConfig(): Observable<any> {
        const headers = new HttpHeaders().set('x-organisation', 'ALL');

        return this.http.get(`${this.baseUrl}/configurations/core`, { headers }).pipe(map((r) => r));
    }

    getUserFamilyLinks(userId: string) {
        return this.http.get(`${this.baseUrl}/profile/family/admin/${userId}`).pipe(map((r) => r as Link[]));
    }

    public postNewFamilyLink(userId: string, familyLink: PostNewFamilyLink): Observable<any> {
        return this.http.post(`${this.baseUrl}/profile/family/admin/${userId}`, familyLink).pipe(map((response: any) => response as boolean));
    }

    public postDocuments(document: any): Observable<string[]> {
        const formData = new FormData();
        formData.append('file', document);
        return this.http.post(`${this.baseUrl}/requests/fileupload/temp`, formData).pipe(map((response: any) => response as string[]));
    }

    public searchUser(user: UserSearchQuery) {
        let params = new HttpParams();
        // Set payload data as query parameters
        for (const key in user) {
            if (user.hasOwnProperty(key)) {
                params = params.set(key, user[key]);
            }
        }

        return this.http
            .get(`${this.baseUrl}/profile/family/admin/search`, {
                params,
            })
            .pipe(map((r) => r as Link[]));
    }

    public postUserFamilyLink(payload: PostUserFamilyLinkPayload, sourceId, targetId) {
        return this.http.post(`${this.baseUrl}/profile/family/admin/${sourceId}/${targetId}`, payload).pipe(map((r) => r));
    }

    public postSubscribeCards(payload: SubscribeCardPayload) {
        return this.http.post(`${this.baseUrl}/cards`, payload).pipe(map((response) => response as UserCards));
    }

    public openCameraProfileError(error: string): void {}

    getUserFamilyLinksItem(userId: string) {
        return this.http.get(`${this.baseUrl}/profile/family/admin/${userId}`).pipe(map((r) => r as GetFamilyLinksItem[]));
    }

    addFamilyLink(sourceId: string, addLinkPayload: PostFamilyLinkPayload): Observable<GetFamilyLinksItem> {
        return this.http.post(`${this.baseUrl}/profile/family/admin/${sourceId}`, addLinkPayload).pipe(map((r) => r as GetFamilyLinksItem));
    }

    modifyFamilyLink(sourceId: string, updateLinkPayload: UpdateFamilyLinkPayload): Observable<GetFamilyLinksItem> {
        return this.http.patch(`${this.baseUrl}/profile/family/admin/${sourceId}`, updateLinkPayload).pipe(map((r) => r as GetFamilyLinksItem));
    }

    removeFamilyLink(sourceId: string, targetId: string): Observable<any> {
        return this.http.delete(`${this.baseUrl}/profile/family/admin/${sourceId}/${targetId}`);
    }

    public getUserSubscriptions(userId: string) {
        return this.http.get(`${this.baseUrl}/profile/admin/user/${userId}/subscriptions`).pipe(map((res) => res as GetUserSubscriptionsResponse));
    }

    public postPayment(payload: PaymentPayload<ItemSubscriptionPayload>, userId: string) {
        return this.http.post(`${this.baseUrl}/payments/admin/user/${userId}`, payload).pipe(map((res) => res as any));
    }

    public patchSubscription(userId: string, subscriptionId: string, payload: any) {
        return this.http.patch(`${this.baseUrl}/profile/admin/user/${userId}/subscriptions/${subscriptionId}`, payload).pipe(map((res) => res as any));
    }

    public deleteSubscription(userId: string, subscriptionId: string) {
        return this.http.delete(`${this.baseUrl}/profile/admin/user/${userId}/subscriptions/${subscriptionId}`).pipe(map((res) => res as any));
    }

    public getUserReceipts(id: string, page = 1, pageSize = 10) {
        return this.http
            .get(`${this.baseUrl}/payments/history/${id}?page=${page}&limit=${pageSize}`)
            .pipe(map((r) => r as BcitiPaginatedResult<ReceiptResponse<ReceiptItemSubscriptionResponse | ReceiptItemParkingResponse | ReceiptItemPrepaidParkingResponse>>));
    }

    public getRequests(page = 1, pageSize = 10, userId: string) {
        return this.http.get(`${this.baseUrl}/requests/admin/user/${userId}?page=${page + 1}&limit=${pageSize}`).pipe(map((response: any) => response as GetUserRequests));
    }

    public getRequestDetail(id: string) {
        return this.http.get(`${this.baseUrl}/requests/admin/${id}`).pipe(map((response: any) => response as UserRequest));
    }

    public getUserVehicules(): Observable<Vehicule[]> {
        const url = `${this.apiUrl}${this._user}/vehicle`;
        return this.http.get<Vehicule[]>(url);
    }

    public addUserVehicule(data: Vehicule) {
        const url = `${this.apiUrl}${this._user}/vehicle`;
        this.http.post(url, data, this.requestOptions).subscribe((response) => {});
    }

    public updateUserVehicule(data: Vehicule[]) {
        const url = `${this.apiUrl}${this._user}/vehicle`;
        this.http.put(url, JSON.stringify(data), this.requestOptions).subscribe();
    }

    public deleteUserVehicule(id: number) {
        const url = `${this.apiUrl}${this._user}/vehicle`;
        this.http.delete(`${url}/${id}`).subscribe();
    }

    public getPermits(vehicle: Vehicule): Observable<any> {
        return this.http.post(
            this.permitUrl,
            {
                params: {
                    action: 'getVehiclePermits',
                    vehicleArray: vehicle,
                },
            },
            this.requestOptions,
        );
    }

    public addParking(data: any) {
        const url = `${this.apiUrl}${this._user}/organisation/${this._organisation}`;
        return this.http.put(url, { montant: data }, this.requestOptions);
    }

    public getHistory() {
        const url = `${this.apiUrl}${this._user}/organisation/${this._organisation}`;
        return this.http.get(url);
    }

    public searchAdmins(query: string, limit = 10): Observable<any> {
        return this.getResolvedOrgTag$().pipe(
            mergeMap(
                (orgKey) =>
                    this.http.get(`${this.baseUrl}/profile/search-admins?q=${encodeURIComponent(query)}&limit=${limit}`, {
                        headers: { 'x-organisation': orgKey },
                    }) as Observable<any>,
            ),
            catchError((e) => {
                console.error(e);
                return of([]);
            }),
        );
    }

    public getUserActivity(userId: string): Observable<UserActivityResponse> {
        return this.getResolvedOrgTag$().pipe(
            mergeMap((orgKey) =>
                this.http.get(`${this.baseUrl}/profile/user-activity/${userId}`, {
                    headers: { 'x-organisation': orgKey },
                }),
            ),
            map((response) => response as UserActivityResponse),
            catchError((e) => {
                console.error(e);
                return of({ user: null, activities: [] } as UserActivityResponse);
            }),
        );
    }

    public getPendingRequestCounts(): Observable<PendingRequestCounts> {
        return this.getResolvedOrgTag$().pipe(
            mergeMap((orgKey) =>
                this.http.get(`${this.baseUrl}/profile/admin/pending-request-counts`, {
                    headers: { 'x-organisation': orgKey },
                }),
            ),
            map((response) => response as PendingRequestCounts),
            catchError((e) => {
                console.error(e);
                return of({ pendingCardRequests: 0, pendingFamilyLinkRequests: 0, pendingResidences: 0 });
            }),
        );
    }

    deleteUserAddress(userId: string, addressId: string): Observable<void> {
        const url = `${this.baseUrl}/profile/addresses/user/${userId}/address/${addressId}`;
        return this.http.delete<void>(url);
    }

    setMainAddress(userId: string, addressId: string): Observable<any> {
        const url = `${this.baseUrl}/profile/addresses/user/${userId}/main/${addressId}`;
        return this.http.patch(url, {});
    }

    public registerFamilyZitadelUser(payload: { email: string; firstName: string; lastName: string }): Observable<{ userId: string }> {
        return this.http.post<{ userId: string }>(`${this.authServiceUrl}/auth/register/family`, payload);
    }

    public updateZitadelEmail(email: string | null, newEmail: string | null, orgId: string, bcitiUserId?: string): Observable<void> {
        const payload: Record<string, string | null> = { email, newEmail, orgId };
        if (bcitiUserId) {
            payload.bcitiUserId = bcitiUserId;
        }
        return this.http.patch<void>(`${this.authServiceUrl}/auth/admin/users/email`, payload);
    }

    public searchBeforeCreate(userId: string, payload: any): Observable<CardSearchResponse> {
        return this.http.post(`${this.baseUrl}/cards/search/${userId}`, payload).pipe(map((response) => response as CardSearchResponse));
    }

    public adminPreCreateVerification(userId: string, cardData: { firstName?: string; lastName?: string; birthDate?: string; sex?: string }): Observable<CardVerificationResponse> {
        return this.http.post(`${this.baseUrl}/cards/admin/verify-create/${userId}`, cardData).pipe(map((response) => response as CardVerificationResponse));
    }
}

export const USER_SERVICE_PROVIDER = {
    provide: UserService,
    useClass: UserServiceImpl,
};
